'use client';

import { createContext, ReactNode, useContext } from "react";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType,
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import { SitePricing, SitePricingInput } from "@/app/lib/generated/graphql-types";
import { SitePricingEditable } from "@/app/features/sites/pricing/types/types";
import { SitePricingSchema } from "@/app/features/sites/pricing/types/schema";
import { getSitePricing } from "@/app/features/sites/pricing/data-access/queries";
import { updateSitePricing } from "@/app/features/sites/pricing/data-access/mutations";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import { sitePricingInputFrom } from "@/app/features/sites/pricing/data-access/mapper";
import { sitePricingEditableFrom } from "@/app/features/sites/pricing/types/mapper";

const PricingEditorContext = createContext<UseSingleTypeEditorType<SitePricingEditable> | undefined>(undefined);

export function usePricingEditor() {
    const context = useContext(PricingEditorContext);

    if (!context) {
        throw new Error("usePricingEditor must be used within a PricingEditorProvider");
    }

    return context;
}

interface PricingEditorProviderProps {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}

export default function PricingEditorProvider({
    children,
    isDraftModeEnabled,
}: PricingEditorProviderProps) {
    const editor = useSingleTypeEditor<SitePricing, SitePricingEditable, SitePricingInput>({
        schema: SitePricingSchema,
        loadEntity: getSitePricing,
        update: updateSitePricing,
        toEditable: sitePricingEditableFrom,
        toInput: sitePricingInputFrom,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <PricingEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={editor.discardChanges}
            />
        </PricingEditorContext.Provider>
    );
}
